<!--
  SuperAdmin: user detail -- identity, role/capabilities, tenancy,
  activity + security, and the destructive actions.

  Card: SA Users page + per-user detail -- https://trello.com/c/tRC6K0Zn
  Backend: api/src/endpoints/superadmin_users.py

  Four sections, matching the spec:

    1. Identity + edit   -- first/last name, email, username, active
    2. Role + capabilities -- the role and what it grants, plus any
                              per-account overrides
    3. Tenancy           -- distributor / client / store scoping
    4. Activity + security -- last login, audit entries, force password
                              reset, impersonate, deactivate, delete

  THE DELETE GUARD (load-bearing)
  ===============================

  Hard delete is offered ONLY when the server reports zero referencing
  rows. `delete_eligibility` arrives with the detail payload, so the
  control's state is decided BEFORE it is rendered -- when the account
  is referenced, the button is DISABLED and the blocking tables are
  listed with their row counts, alongside the instruction to deactivate
  instead. The UI never issues a delete it expects to fail; the server
  re-runs the same scan regardless, so this is defense in depth rather
  than the only guard.

  Password: there is deliberately no "set password" field here. An SA
  sends a set-password link instead, so no SA-chosen credential exists.

  Access: route-gated on superadmin.settings (this page mutates).

  ASCII-only.
-->
<template>
    <div data-testid="sa-user-detail">
        <p v-if="loading" class="sa-state" data-testid="sa-user-loading">
            Loading...
        </p>

        <p
            v-else-if="loadError"
            class="sa-state sa-state--error"
            data-testid="sa-user-error"
        >
            {{ loadError }}
        </p>

        <template v-else>
            <div class="sa-user__header">
                <h1 class="sa-page-title">
                    {{ displayName }}
                </h1>
                <button
                    type="button"
                    class="sa-btn"
                    data-testid="sa-user-back"
                    @click="$router.push('/admin/users')"
                >
                    Back to users
                </button>
            </div>

            <p
                v-if="errorMsg"
                class="sa-banner sa-banner--error"
                data-testid="sa-user-error"
            >
                {{ errorMsg }}
            </p>
            <p
                v-if="successMsg"
                class="sa-banner sa-banner--ok"
                data-testid="sa-user-success"
            >
                {{ successMsg }}
            </p>

            <!-- 1. Identity ------------------------------------------- -->
            <section class="sa-card" data-testid="sa-user-identity">
                <h2 class="sa-card__title">Identity</h2>

                <label class="sa-field">
                    <span>First name</span>
                    <input
                        class="sa-input"
                        data-testid="sa-user-first-name"
                        v-model="form.first_name"
                    />
                </label>

                <label class="sa-field">
                    <span>Last name</span>
                    <input
                        class="sa-input"
                        data-testid="sa-user-last-name"
                        v-model="form.last_name"
                    />
                </label>

                <label class="sa-field">
                    <span>Email</span>
                    <input
                        class="sa-input"
                        type="email"
                        data-testid="sa-user-email"
                        v-model="form.email"
                    />
                </label>

                <label class="sa-field">
                    <span>Username</span>
                    <input
                        class="sa-input"
                        data-testid="sa-user-username"
                        :value="form.username"
                        @input="onUsernameInput"
                    />
                </label>
                <!--
                  Live availability warning. UX only -- the server's
                  unique constraint is the real guard, and a save can
                  still return 409 after this said "available".
                -->
                <p
                    v-if="usernameTaken"
                    class="sa-warn"
                    data-testid="sa-user-username-taken"
                >
                    That username is taken.
                </p>

                <label class="sa-field sa-field--inline">
                    <input
                        type="checkbox"
                        data-testid="sa-user-active"
                        v-model="form.active"
                    />
                    <span>Active</span>
                </label>

                <div class="sa-actions">
                    <button
                        type="button"
                        class="sa-btn sa-btn--primary"
                        data-testid="sa-user-save"
                        :disabled="saving"
                        @click="save"
                    >
                        Save changes
                    </button>
                </div>
            </section>

            <!-- 2. Role + capabilities -------------------------------- -->
            <section class="sa-card" data-testid="sa-user-role">
                <h2 class="sa-card__title">Role and capabilities</h2>
                <p class="sa-kv">
                    <span class="sa-kv__k">Role</span>
                    <span class="sa-kv__v">{{ user.role_name || "--" }}</span>
                </p>

                <p v-if="!capabilities.length" class="sa-muted">
                    No capabilities resolved for this account.
                </p>
                <ul v-else class="sa-caps">
                    <li
                        v-for="cap in capabilities"
                        :key="cap.key"
                        :data-testid="`sa-user-cap-${cap.key}`"
                        class="sa-caps__item"
                    >
                        <code>{{ cap.key }}</code>
                        <span class="sa-caps__src">{{
                            sourceLabel(cap.source)
                        }}</span>
                    </li>
                </ul>

                <h3 class="sa-card__subtitle">Per-account overrides</h3>
                <p v-if="!overrides.length" class="sa-muted">
                    No per-account overrides.
                </p>
                <ul v-else class="sa-caps">
                    <li
                        v-for="ov in overrides"
                        :key="ov.id"
                        :data-testid="`sa-user-override-${ov.id}`"
                        class="sa-caps__item"
                    >
                        <code>{{ ov.capability_key }}</code>
                        <span class="sa-caps__src">{{ ov.effect }}</span>
                        <span v-if="ov.reason" class="sa-muted">
                            {{ ov.reason }}
                        </span>
                    </li>
                </ul>
            </section>

            <!-- 3. Tenancy -------------------------------------------- -->
            <section class="sa-card" data-testid="sa-user-tenancy">
                <h2 class="sa-card__title">Tenancy</h2>
                <p class="sa-kv">
                    <span class="sa-kv__k">Distributor</span>
                    <span class="sa-kv__v">
                        <DistributorLink
                            :distributor-id="user.distributor_id"
                            :name="user.distributor_name"
                            fallback="--"
                        />
                    </span>
                </p>
                <p class="sa-kv">
                    <span class="sa-kv__k">Client</span>
                    <span class="sa-kv__v">{{ user.client_name || "--" }}</span>
                </p>
                <p class="sa-kv">
                    <span class="sa-kv__k">Stores</span>
                    <span class="sa-kv__v">{{ storeLabel }}</span>
                </p>
            </section>

            <!-- 4. Activity + security -------------------------------- -->
            <section class="sa-card" data-testid="sa-user-activity">
                <h2 class="sa-card__title">Activity and security</h2>
                <p class="sa-kv">
                    <span class="sa-kv__k">Last login</span>
                    <span class="sa-kv__v" data-testid="sa-user-last-login">{{
                        formatDate(user.last_login_at)
                    }}</span>
                </p>

                <!--
                  Second factor. The API has returned `user.mfa` and
                  exposed the reset endpoint since MFA shipped; nothing
                  read either, so a user who lost their authenticator
                  could not be helped from the app even though the server
                  could do it. This is that recovery path.

                  Counts only -- no secret material. "Is this account
                  protected?" and "why can this person not get in?" are
                  both answerable from counts and timestamps.
                -->
                <p class="sa-kv">
                    <span class="sa-kv__k">Second factor</span>
                    <span class="sa-kv__v" data-testid="sa-user-mfa-status">
                        <template v-if="mfa.enabled">
                            On -- {{ mfa.recovery_codes_remaining }} recovery
                            codes left, {{ mfa.passkey_count }} passkeys
                        </template>
                        <template v-else-if="mfa.passkey_count">
                            Authenticator off --
                            {{ mfa.passkey_count }} passkeys
                        </template>
                        <template v-else>
                            Off -- none registered
                        </template>
                    </span>
                </p>

                <div class="sa-actions">
                    <!--
                      Offered only when there is something to clear.
                      Passkey-only counts: "authenticator off" still means
                      the user cannot get in without their device.
                    -->
                    <button
                        v-if="mfa.enabled || mfa.passkey_count"
                        type="button"
                        class="sa-btn sa-btn--warn"
                        data-testid="sa-user-mfa-reset"
                        :disabled="busy"
                        @click="onResetMfa"
                    >
                        Clear second factor
                    </button>
                    <button
                        type="button"
                        class="sa-btn"
                        data-testid="sa-user-force-reset"
                        :disabled="busy"
                        @click="onForceReset"
                    >
                        Send set-password email
                    </button>
                    <button
                        type="button"
                        class="sa-btn"
                        data-testid="sa-user-impersonate"
                        :disabled="!impersonationTarget || busy"
                        :title="impersonateTitle"
                        @click="onImpersonate"
                    >
                        Impersonate
                    </button>
                    <button
                        v-if="user.active"
                        type="button"
                        class="sa-btn sa-btn--warn"
                        data-testid="sa-user-deactivate"
                        :disabled="busy"
                        @click="onDeactivate"
                    >
                        Deactivate
                    </button>
                    <button
                        v-else
                        type="button"
                        class="sa-btn"
                        data-testid="sa-user-activate"
                        :disabled="busy"
                        @click="onActivate"
                    >
                        Reactivate
                    </button>
                </div>

                <h3 class="sa-card__subtitle">Recent audit entries</h3>
                <p v-if="!auditEntries.length" class="sa-muted">
                    No audit entries for this user.
                </p>
                <ul v-else class="sa-audit">
                    <li
                        v-for="entry in auditEntries"
                        :key="entry.id"
                        :data-testid="`sa-user-audit-${entry.id}`"
                        class="sa-audit__item"
                    >
                        <span class="sa-audit__when">{{
                            formatDate(entry.created_at)
                        }}</span>
                        <span>{{ entry.event_category }}</span>
                        <span>{{ entry.entity_type }}</span>
                        <span>{{ entry.change_type }}</span>
                        <span v-if="entry.reason" class="sa-muted">{{
                            entry.reason
                        }}</span>
                    </li>
                </ul>
            </section>

            <!-- Danger zone: hard delete ------------------------------ -->
            <section class="sa-card sa-card--danger" data-testid="sa-user-danger">
                <h2 class="sa-card__title">Delete account</h2>
                <p class="sa-muted">
                    Hard delete is permitted only for an account that has
                    never been used -- one with no referencing records
                    anywhere.
                </p>

                <!--
                  The blocking reason is rendered whenever the server
                  says the account is referenced, and the button is
                  disabled in the same state. Deciding here (rather than
                  attempting the delete and reporting the failure) is
                  what makes orphaning structurally impossible.
                -->
                <p
                    v-if="!deleteEligibility.deletable"
                    class="sa-warn"
                    data-testid="sa-user-delete-blocked"
                >
                    This account has been used and cannot be deleted.
                    Deactivate it instead. Referencing records:
                    <span
                        v-for="ref in deleteEligibility.references"
                        :key="`${ref.table}.${ref.column}`"
                    >
                        {{ ref.table }} ({{ ref.count }});
                    </span>
                </p>

                <button
                    type="button"
                    class="sa-btn sa-btn--danger"
                    data-testid="sa-user-delete"
                    :disabled="!deleteEligibility.deletable || busy"
                    @click="onDelete"
                >
                    Delete permanently
                </button>
            </section>
        </template>
    </div>
</template>

<script>
import DistributorLink from "@/components/superadmin/DistributorLink.vue";
import {
    getUser,
    updateUser,
    checkUsernameAvailable,
    deactivateUser,
    activateUser,
    forcePasswordReset,
    resetUserMfa,
    deleteUser,
} from "@/services/superadminUsersService";
import { useImpersonationStore } from "@/stores/impersonation";

const USERNAME_DEBOUNCE_MS = 300;

export default {
    components: { DistributorLink },
    name: "SuperAdminUserDetail",
    props: {
        // Passed as a route prop so tests can mount without a router.
        id: { type: [String, Number], default: null },
    },
    setup() {
        const impersonationStore = useImpersonationStore();
        return { impersonationStore };
    },
    data() {
        return {
            loading: true,
            loadError: "",
            errorMsg: "",
            successMsg: "",
            saving: false,
            busy: false,
            user: {},
            capabilities: [],
            overrides: [],
            auditEntries: [],
            // Default to NOT deletable: if eligibility is missing for any
            // reason, the safe state is refusing the delete, never
            // offering it.
            deleteEligibility: { deletable: false, references: [] },
            form: {
                first_name: "",
                last_name: "",
                email: "",
                username: "",
                active: true,
            },
            usernameTaken: false,
            usernameTimer: null,
        };
    },
    computed: {
        /**
         * The account's second-factor status, normalised.
         *
         * Defaulted rather than assumed present: `user` starts empty
         * while the detail request is in flight, and an older payload
         * without an `mfa` block must render "off" instead of throwing
         * on a missing property mid-render.
         */
        mfa() {
            const m = (this.user && this.user.mfa) || {};
            return {
                enabled: !!m.enabled,
                enabled_at: m.enabled_at || null,
                last_verified_at: m.last_verified_at || null,
                recovery_codes_remaining: m.recovery_codes_remaining || 0,
                passkey_count: m.passkey_count || 0,
            };
        },

        accountId() {
            if (this.id !== null && this.id !== undefined && this.id !== "") {
                return this.id;
            }
            return this.$route && this.$route.params
                ? this.$route.params.id
                : null;
        },
        displayName() {
            const name = [this.user.first_name, this.user.last_name]
                .filter(Boolean)
                .join(" ")
                .trim();
            return name || this.user.username || "User";
        },
        storeLabel() {
            const stores = this.user.stores || [];
            return stores.length ? stores.map((s) => s.name).join(", ") : "--";
        },
        // Impersonation in this product is per-TENANT, never per-account:
        // the bearer token is stamped with a distributor id or a client
        // id, and the impersonated capability set is derived from that
        // tenant. So "impersonate this user" resolves to "enter the
        // tenant this user belongs to". A distributor id wins when the
        // account carries both, because the distributor app is the
        // broader surface. An account with neither (a platform-level SA)
        // has no tenant to enter and is not impersonatable.
        impersonationTarget() {
            if (this.user.distributor_id) {
                return {
                    mode: "distributor",
                    id: this.user.distributor_id,
                    name: this.user.distributor_name || "",
                    landing: "/distributor",
                };
            }
            if (this.user.client_id) {
                return {
                    mode: "client",
                    id: this.user.client_id,
                    name: this.user.client_name || "",
                    landing: "/portal",
                };
            }
            return null;
        },
        impersonateTitle() {
            return this.impersonationTarget
                ? `Enter ${this.impersonationTarget.name || "this tenant"} as an impersonated session`
                : "This account belongs to no distributor or client, so there is no tenant to impersonate";
        },
    },
    async mounted() {
        await this.load();
    },
    beforeUnmount() {
        if (this.usernameTimer) {
            clearTimeout(this.usernameTimer);
        }
    },
    methods: {
        sourceLabel(source) {
            const labels = {
                template: "role",
                tenant_materialized: "tenant",
                account_override: "override",
            };
            return labels[source] || source || "";
        },

        formatDate(value) {
            if (!value) {
                return "Never";
            }
            return String(value).slice(0, 10);
        },

        async load() {
            this.loading = true;
            this.loadError = "";
            try {
                const resp = await getUser(this.accountId);
                const data = resp && resp.data ? resp.data : resp;
                this.applyDetail(data);
            } catch (e) {
                this.loadError = "Could not load this user.";
            } finally {
                this.loading = false;
            }
        },

        applyDetail(data) {
            this.user = (data && data.user) || {};
            this.capabilities = (data && data.capabilities) || [];
            this.overrides = (data && data.overrides) || [];
            this.auditEntries = (data && data.audit_entries) || [];
            this.deleteEligibility = (data && data.delete_eligibility) || {
                deletable: false,
                references: [],
            };
            this.form = {
                first_name: this.user.first_name || "",
                last_name: this.user.last_name || "",
                email: this.user.email || "",
                username: this.user.username || "",
                active: !!this.user.active,
            };
            this.usernameTaken = false;
        },

        onUsernameInput(event) {
            this.form.username = event.target.value;
            if (this.usernameTimer) {
                clearTimeout(this.usernameTimer);
            }
            const candidate = this.form.username;
            // Unchanged username is trivially available to itself.
            if (!candidate || candidate === this.user.username) {
                this.usernameTaken = false;
                return;
            }
            this.usernameTimer = setTimeout(async () => {
                try {
                    const resp = await checkUsernameAvailable(
                        candidate,
                        this.user.id
                    );
                    const data = resp && resp.data ? resp.data : resp;
                    // Ignore a response that lost the race to newer input.
                    if (candidate === this.form.username) {
                        this.usernameTaken = data && data.available === false;
                    }
                } catch (e) {
                    // A failed availability probe is a UX nicety, not a
                    // gate -- leave the warning off and let the server's
                    // constraint decide on save.
                    this.usernameTaken = false;
                }
            }, USERNAME_DEBOUNCE_MS);
        },

        serverMessage(e, fallback) {
            const msg =
                (e && e.response && e.response.data && e.response.data.error) ||
                (e && e.data && e.data.error);
            return msg || fallback;
        },

        async save() {
            this.saving = true;
            this.errorMsg = "";
            this.successMsg = "";
            try {
                await updateUser(this.accountId, {
                    first_name: this.form.first_name,
                    last_name: this.form.last_name,
                    email: this.form.email,
                    username: this.form.username,
                    active: this.form.active,
                });
                this.successMsg = "Changes saved.";
                await this.load();
            } catch (e) {
                this.errorMsg = this.serverMessage(
                    e,
                    "Could not save changes. Please try again."
                );
            } finally {
                this.saving = false;
            }
        },

        async runAction(fn, okMessage, failMessage) {
            this.busy = true;
            this.errorMsg = "";
            this.successMsg = "";
            try {
                await fn();
                this.successMsg = okMessage;
                return true;
            } catch (e) {
                this.errorMsg = this.serverMessage(e, failMessage);
                return false;
            } finally {
                this.busy = false;
            }
        },

        async onDeactivate() {
            const ok = await this.runAction(
                () => deactivateUser(this.accountId),
                "User deactivated.",
                "Could not deactivate this user."
            );
            if (ok) {
                await this.load();
            }
        },

        async onActivate() {
            const ok = await this.runAction(
                () => activateUser(this.accountId),
                "User reactivated.",
                "Could not reactivate this user."
            );
            if (ok) {
                await this.load();
            }
        },

        async onForceReset() {
            await this.runAction(
                () => forcePasswordReset(this.accountId),
                "Set-password email sent.",
                "Could not send the set-password email."
            );
        },

        // Not routed through runAction: that helper discards the response,
        // and the refreshed `mfa` block IS the point. Security state is
        // read back from the server rather than assumed -- a locally
        // guessed "off" after a call that actually failed would tell an
        // admin the account is recoverable when it is not.
        async onResetMfa() {
            this.busy = true;
            this.errorMsg = "";
            this.successMsg = "";
            try {
                const resp = await resetUserMfa(this.accountId);
                const fresh = resp && resp.data && resp.data.mfa;
                if (fresh) {
                    this.user = { ...this.user, mfa: fresh };
                }
                this.successMsg =
                    "Second factor cleared. The user can sign in with their password and set it up again.";
            } catch (e) {
                this.errorMsg = this.serverMessage(
                    e,
                    "Could not clear the second factor."
                );
            } finally {
                this.busy = false;
            }
        },

        // Impersonate the TENANT this user belongs to, using the same
        // store actions the SA Distributors and SA Clients lists use.
        // The previous implementation pushed `/admin/impersonate`, a
        // route that exists nowhere in the router, so the button was a
        // dead end. There is no per-ACCOUNT impersonation mechanism to
        // mirror -- the token is stamped with a distributor or client
        // id -- so entering the user's tenant is the real action.
        //
        // A hard navigation (not $router.push) is deliberate and
        // matches both precedents: the impersonated capability set
        // comes back from the backend on the next request, so the app
        // must re-boot rather than soft-navigate with a stale one.
        async onImpersonate() {
            const target = this.impersonationTarget;
            if (!target || this.busy) {
                return;
            }
            const ok = await this.runAction(
                () =>
                    target.mode === "distributor"
                        ? this.impersonationStore.startImpersonation(
                              target.id,
                              target.name
                          )
                        : this.impersonationStore.startClientImpersonation(
                              target.id,
                              target.name
                          ),
                `Impersonating ${target.name || "tenant"}.`,
                "Could not start an impersonation session for this user."
            );
            if (ok && typeof window !== "undefined" && window.location) {
                window.location.assign(target.landing);
            }
        },

        async onDelete() {
            // Belt and braces: the button is already disabled in this
            // state, but never let a delete leave the page when the
            // account is known to be referenced.
            if (!this.deleteEligibility.deletable) {
                return;
            }
            if (
                typeof window !== "undefined" &&
                typeof window.confirm === "function" &&
                !window.confirm(
                    `Permanently delete ${this.displayName}? This cannot be undone.`
                )
            ) {
                return;
            }
            const ok = await this.runAction(
                () => deleteUser(this.accountId),
                "User deleted.",
                "Could not delete this user."
            );
            if (ok) {
                this.$router.push("/admin/users");
            }
        },
    },
};
</script>

<style scoped>
.sa-page-title {
    font-size: 22px;
    font-weight: 600;
    margin: 0;
    color: #1a202c;
}
.sa-user__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
}
.sa-card {
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 16px;
    margin-bottom: 16px;
    background: #fff;
}
.sa-card--danger {
    border-color: #feb2b2;
    background: #fffafa;
}
.sa-card__title {
    font-size: 15px;
    font-weight: 600;
    margin: 0 0 12px;
    color: #2d3748;
}
.sa-card__subtitle {
    font-size: 13px;
    font-weight: 600;
    margin: 16px 0 8px;
    color: #4a5568;
}
.sa-field {
    display: block;
    margin-bottom: 10px;
    font-size: 13px;
    color: #4a5568;
}
.sa-field--inline {
    display: flex;
    align-items: center;
    gap: 8px;
}
.sa-field > span {
    display: block;
    margin-bottom: 4px;
}
.sa-field--inline > span {
    margin-bottom: 0;
}
.sa-input {
    font-size: 13px;
    padding: 6px 10px;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    background: #fff;
    color: #2d3748;
    min-width: 260px;
}
.sa-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
}
.sa-btn {
    font-size: 13px;
    font-weight: 500;
    padding: 7px 14px;
    border-radius: 4px;
    border: 1px solid #e2e8f0;
    background: #fff;
    color: #2d3748;
    cursor: pointer;
}
.sa-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
.sa-btn--primary {
    background: #2b6cb0;
    border-color: #2b6cb0;
    color: #fff;
}
.sa-btn--warn {
    border-color: #dd6b20;
    color: #dd6b20;
}
.sa-btn--danger {
    background: #c53030;
    border-color: #c53030;
    color: #fff;
}
.sa-state {
    font-size: 14px;
    color: #4a5568;
    padding: 12px 0;
}
.sa-state--error {
    color: #c53030;
}
.sa-banner {
    font-size: 13px;
    padding: 8px 12px;
    border-radius: 4px;
    margin-bottom: 12px;
}
.sa-banner--ok {
    background: #f0fff4;
    border: 1px solid #9ae6b4;
    color: #22543d;
}
.sa-banner--error {
    background: #fff5f5;
    border: 1px solid #feb2b2;
    color: #c53030;
}
.sa-warn {
    font-size: 13px;
    color: #c53030;
    margin: 6px 0 10px;
}
.sa-muted {
    font-size: 12px;
    color: var(--tf-fg-4);
}
.sa-kv {
    font-size: 13px;
    margin: 0 0 6px;
    display: flex;
    gap: 12px;
}
.sa-kv__k {
    color: var(--tf-fg-3);
    min-width: 110px;
}
.sa-kv__v {
    color: #2d3748;
}
.sa-caps,
.sa-audit {
    list-style: none;
    margin: 0;
    padding: 0;
}
.sa-caps__item,
.sa-audit__item {
    font-size: 12px;
    padding: 4px 0;
    border-bottom: 1px solid #edf2f7;
    display: flex;
    gap: 10px;
    align-items: center;
}
.sa-caps__src {
    color: var(--tf-fg-4);
    font-size: 11px;
}
.sa-audit__when {
    color: var(--tf-fg-4);
    min-width: 90px;
}
</style>
